import { useEffect, useState } from "react";
import { Mail, MapPin, Facebook, Youtube, Twitter, Instagram, Linkedin } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { OFundedLogo } from "@/components/BrandLogo";
import { SOCIAL_LINKS } from "@/data/site";
import api from "@/lib/api";

const SOCIALS = [
  { id: "facebook",  label: "Facebook",  url: SOCIAL_LINKS.facebook,  Icon: Facebook },
  { id: "youtube",   label: "YouTube",   url: SOCIAL_LINKS.youtube,   Icon: Youtube },
  { id: "twitter",   label: "X",         url: SOCIAL_LINKS.twitter,   Icon: Twitter },
  { id: "instagram", label: "Instagram", url: SOCIAL_LINKS.instagram, Icon: Instagram },
  { id: "linkedin",  label: "LinkedIn",  url: SOCIAL_LINKS.linkedin,  Icon: Linkedin },
];

function SocialRow() {
  return (
    <div className="mt-5 flex flex-wrap gap-2.5 justify-center lg:justify-start" data-testid="footer-social-cluster">
      {SOCIALS.map(({ id, label, url, Icon }) => (
        <a
          key={id}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          title={label}
          data-testid={`social-${id}`}
          className="w-10 h-10 rounded-xl border grid place-items-center transition-all hover:scale-105"
          style={{
            borderColor: "color-mix(in srgb, var(--brand) 18%, var(--line-strong))",
            background: "color-mix(in srgb, var(--surface) 60%, transparent)",
            color: "var(--text-2)",
          }}
        >
          <Icon size={15} strokeWidth={1.7} />
        </a>
      ))}
    </div>
  );
}

function ColumnHeading({ children }) {
  return (
    <h3 className="font-mono text-[12px] font-medium tracking-[0.04em] text-text-2 mb-4 text-center lg:text-left">
      {children}
    </h3>
  );
}

function FooterLink({ to, children, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="text-[13.5px] text-text-2 hover:text-text-1 transition-colors block leading-relaxed text-center lg:text-left"
    >
      {children}
    </Link>
  );
}

export default function Footer() {
  const { t } = useTranslation();
  // FSC Mauritius licence block · visibility decided server-side
  // (admin toggle + country hide-list + optional VPN hiding).
  const [showMauritius, setShowMauritius] = useState(false);
  useEffect(() => {
    api.get("/footer-license")
      .then(({ data }) => setShowMauritius(!!data?.visible))
      .catch(() => {});
  }, []);
  const year = new Date().getFullYear();
  return (
    <footer data-testid="site-footer" className="relative mt-24" style={{ background: "color-mix(in srgb, var(--surface) 60%, transparent)" }}>
      {/* Top accent line */}
      <div className="h-px w-full" style={{ background: "linear-gradient(90deg, transparent, color-mix(in srgb, var(--brand) 45%, transparent), transparent)" }} />

      {/* Main columns · mobile = 2-up link grid, desktop = 5-col spread */}
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 grid grid-cols-2 lg:grid-cols-5 gap-x-6 gap-y-10 lg:gap-10">
        {/* Brand block — full width + centered on mobile, spans 2 cols left-aligned on lg */}
        <div className="col-span-2 lg:col-span-2 max-w-md mx-auto lg:mx-0 text-center lg:text-left">
          <Link to="/" className="inline-flex items-center mb-5">
            <OFundedLogo className="h-8 w-auto" />
          </Link>
          <p className="text-text-2 text-sm leading-relaxed">
            {t("footer.brandLine", "An institutional broker for serious traders. Trade FX, metals, indices, energies, share CFDs and crypto with raw spreads and MT5 execution.")}
          </p>
          <SocialRow />
          <div className="mt-6 flex items-start gap-2.5 text-text-3 text-[12.5px] justify-center lg:justify-start">
            <MapPin size={14} className="text-brand mt-0.5 shrink-0" />
            <span>{t("footer.address", "John Kennedy, Iris House Kanika Complex, Floor 2, 3106 Limassol, Cyprus")}</span>
          </div>
          <div className="mt-2.5 flex items-center gap-2.5 text-text-3 text-[12.5px] justify-center lg:justify-start">
            <Mail size={14} className="text-brand shrink-0" />
            <a href="mailto:support@ofinancial.markets" className="hover:text-text-1 transition-colors">support@ofinancial.markets</a>
          </div>
        </div>

        <div>
          <ColumnHeading>{t("footer.tradingColumn", "Trading")}</ColumnHeading>
          <ul className="space-y-2.5">
            <li><FooterLink to="/markets/forex"    testId="footer-link-forex">{t("homepage.spreads.categories.forex", "Forex")}</FooterLink></li>
            <li><FooterLink to="/markets/metals"   testId="footer-link-metals">{t("homepage.spreads.categories.metals", "Metals")}</FooterLink></li>
            <li><FooterLink to="/markets/indices"  testId="footer-link-indices">{t("homepage.spreads.categories.indices", "Indices")}</FooterLink></li>
            <li><FooterLink to="/markets/energies" testId="footer-link-energies">{t("homepage.spreads.categories.energies", "Energies")}</FooterLink></li>
            <li><FooterLink to="/markets/shares"   testId="footer-link-shares">{t("footer.sharesCFDs", "Shares CFDs")}</FooterLink></li>
            <li><FooterLink to="/markets/crypto"   testId="footer-link-crypto">{t("homepage.spreads.categories.crypto", "Crypto")}</FooterLink></li>
            <li><FooterLink to="/live-spreads"     testId="footer-link-livespreads">{t("footer.liveSpreads", "Live Spreads")}</FooterLink></li>
          </ul>
        </div>

        <div>
          <ColumnHeading>{t("footer.accountsColumn", "Accounts")}</ColumnHeading>
          <ul className="space-y-2.5">
            <li><FooterLink to="/account-types/classic" testId="footer-link-classic">Classic</FooterLink></li>
            <li><FooterLink to="/account-types/premier" testId="footer-link-premier">Premier</FooterLink></li>
            <li><FooterLink to="/account-types/elite"   testId="footer-link-elite">Elite</FooterLink></li>
            <li><FooterLink to="/account-types/social"  testId="footer-link-social">Social</FooterLink></li>
            <li><FooterLink to="/account-types/islamic" testId="footer-link-islamic">{t("footer.islamic", "Islamic")}</FooterLink></li>
            <li><FooterLink to="/first-deposit-bonus"   testId="footer-link-bonus">{t("footer.firstDepositBonus", "50% First Deposit Bonus")}</FooterLink></li>
            <li><FooterLink to="/funding"               testId="footer-link-funding">{t("footer.fundingWithdrawals", "Funding & Withdrawals")}</FooterLink></li>
          </ul>
        </div>

        <div className="col-span-2 lg:col-span-1">
          <ColumnHeading>{t("footer.company", "Company")}</ColumnHeading>
          <ul className="grid grid-cols-2 gap-x-6 gap-y-2.5 lg:grid-cols-1">
            <li><FooterLink to="/about"           testId="footer-link-about">{t("nav.about", "About")}</FooterLink></li>
            <li><FooterLink to="/company-news"    testId="footer-link-news">{t("footer.companyNews", "Company News")}</FooterLink></li>
            <li><FooterLink to="/regulation"      testId="footer-link-regulation">{t("footer.regulation", "Regulation")}</FooterLink></li>
            <li><FooterLink to="/safety-of-funds" testId="footer-link-safety">{t("footer.safetyOfFunds", "Safety of Funds")}</FooterLink></li>
            <li><FooterLink to="/why-ofinancial"  testId="footer-link-why">{t("footer.whyOFinancial", "Why OFinancial")}</FooterLink></li>
            <li><FooterLink to="/contact"         testId="footer-link-contact">{t("nav.contact", "Contact")}</FooterLink></li>
            <li><FooterLink to="/support"         testId="footer-link-support">{t("footer.support", "Support")}</FooterLink></li>
            <li><FooterLink to="/faq"             testId="footer-link-faq">{t("nav.faq", "FAQ")}</FooterLink></li>
            <li><FooterLink to="/partner"         testId="footer-link-partner">{t("footer.partners", "Partners · IB")}</FooterLink></li>
            <li><FooterLink to="/affiliates"      testId="footer-link-affiliates">{t("footer.affiliates", "Affiliate Program")}</FooterLink></li>
          </ul>
        </div>
      </div>

      {/* Regulatory & legal block */}
      <div className="border-t" style={{ borderColor: "var(--line)" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 text-[12px] leading-relaxed text-text-3 space-y-4 text-center lg:text-left">
          <p data-testid="footer-risk">
            <strong className="text-text-1">{t("footer.riskNotice", "High Risk Investment Notice:")}</strong> {t("footer.riskBody")}
          </p>
          <p>{t("footer.noAdvice")}</p>
          <p>{t("footer.pciNotice")}</p>
          <p>{t("footer.trademark")}</p>
          <p>{t("footer.groupIntro")}</p>
          <ul className="list-none space-y-1.5 lg:list-disc lg:pl-5">
            <li><strong className="text-text-1">OFinancial</strong> {t("footer.groupItem1")}</li>
            <li><strong className="text-text-1">OFMCorp Ltd</strong>, {t("footer.groupItem2")}</li>
            {showMauritius && (
              <li data-testid="footer-mauritius-license">
                <strong className="text-text-1">OFinancial LLC</strong> is regulated by the Financial Services Commission (FSC) Mauritius as an Investment Dealer (Full Service Dealer, excluding Underwriting) with License Number GB25205138.
              </li>
            )}
          </ul>
          <p>{t("footer.jurisdictions")}</p>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t" style={{ borderColor: "var(--line)" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-5 flex flex-col md:flex-row items-center md:items-center md:justify-between gap-3 text-[12px] text-text-3 text-center md:text-left">
          <span data-testid="footer-copyright">{t("footer.copyright", "Copyright © 2023 · {{year}} OMNI Markets LLC. All rights reserved.", { year })}</span>
          <div className="flex flex-wrap items-center justify-center md:justify-end gap-x-3 gap-y-1">
            <Link to="/legal"                  className="hover:text-text-1 transition-colors font-medium">{t("footer.legalCentre", "Legal centre")}</Link>
            <span className="opacity-30">|</span>
            <Link to="/legal/terms"            className="hover:text-text-1 transition-colors">{t("footer.terms", "Terms and Conditions")}</Link>
            <span className="opacity-30">|</span>
            <Link to="/legal/client-agreement" className="hover:text-text-1 transition-colors">{t("footer.clientAgreement", "Client Agreement")}</Link>
            <span className="opacity-30">|</span>
            <Link to="/legal/aml"              className="hover:text-text-1 transition-colors">{t("footer.aml", "AML Policy")}</Link>
            <span className="opacity-30">|</span>
            <Link to="/legal/privacy"          className="hover:text-text-1 transition-colors">{t("footer.privacy", "Privacy Policy")}</Link>
            <span className="opacity-30">|</span>
            <Link to="/legal/cookies"          className="hover:text-text-1 transition-colors">{t("footer.cookies", "Cookie Policy")}</Link>
            <span className="opacity-30">|</span>
            <Link to="/legal/bonus-terms"      className="hover:text-text-1 transition-colors">{t("footer.bonusTerms", "Bonus Terms")}</Link>
            {/* Hidden admin login link · the URL still works (typed directly or
                bookmarked) but it's not surfaced visually in the footer. */}
            <Link to="/admin/login" data-testid="admin-login-footer-link" className="sr-only" aria-hidden="true" tabIndex={-1}>{t("footer.admin", "Admin")}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
